import React, { useState, useEffect, useCallback } from 'react';
import ProtectedRoute from '../../routes/protected';
import { Toast } from 'primereact/toast';
import { Chip } from 'primereact/chip';
import { Dropdown } from 'primereact/dropdown';
import UsePerms from '../../context/userAccess';
import surveyService from '../../services/data/survey.service';
import companyService from '../../services/data/company.service';
import SurveyPageHeader from './SurveyPageHeader';

const TABS = [
    { key: 'pending', label: 'Aguardando resposta' },
    { key: 'answered', label: 'Respondidas' },
    { key: 'expired', label: 'Expiradas' },
];

const STATUS_CHIP = {
    pendente: 'bg-warning text-dark border-0',
    respondida: 'bg-success text-white border-0',
    expirada: 'bg-secondary text-white border-0',
};

function errDetail(err) {
    const d = err.response?.data?.detail;
    if (Array.isArray(d)) return d.join(' ');
    if (typeof d === 'string') return d;
    return err.message || 'Erro';
}

export default function SurveyTriggerMonitoring() {
    const toast = React.useRef(null);
    const VerifyPerms = UsePerms();
    const companyId = localStorage.getItem('companyId');
    const canView = VerifyPerms(['view_surveytemplate']);

    const [activeTab, setActiveTab] = useState('pending');
    const [dispatches, setDispatches] = useState([]);
    const [loading, setLoading] = useState(true);
    const [surveys, setSurveys] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [surveyId, setSurveyId] = useState(null);
    const [departmentId, setDepartmentId] = useState(null);

    useEffect(() => {
        if (!companyId) return;
        surveyService.listSurveys(1, { company_id: companyId })
            .then((r) => {
                const list = r.results || r || [];
                setSurveys([{ title: 'Todas as pesquisas', value: null }, ...list.map((s) => ({ title: s.name, value: s.id }))]);
            })
            .catch(() => {});
        companyService.listDepartmentSimple()
            .then((res) => {
                setDepartments([{ title: 'Todos os departamentos', value: null }, ...res.map((d) => ({ title: d.title, value: d.id }))]);
            })
            .catch(() => {});
    }, [companyId]);

    const load = useCallback(() => {
        if (!companyId || !canView) return;
        setLoading(true);
        const payload = { company_id: companyId, tab: activeTab };
        if (surveyId) payload.survey_id = surveyId;
        if (departmentId) payload.department_id = departmentId;
        surveyService.listTriggerDispatches(payload)
            .then(setDispatches)
            .catch((err) => {
                toast.current?.show({ severity: 'error', summary: 'Erro', detail: errDetail(err) });
            })
            .finally(() => setLoading(false));
    }, [companyId, activeTab, surveyId, departmentId, canView]);

    useEffect(() => { load(); }, [load]);

    if (!canView) {
        return (
            <ProtectedRoute>
                <div className="container-fluid">
                    <SurveyPageHeader title="Monitoramento de Disparos" />
                    <div className="alert alert-warning mx-5 my-4">Sem permissão para acessar este painel.</div>
                </div>
            </ProtectedRoute>
        );
    }

    return (
        <ProtectedRoute>
            <Toast ref={toast} position="top-right" />
            <div className="container-fluid" style={{ paddingBottom: '200px' }}>
                <SurveyPageHeader title="Monitoramento de Disparos Automáticos" />
                <div className="my-4 mx-5 py-3">
                    <div className="d-flex flex-wrap gap-3 mb-4 align-items-center">
                        <Dropdown value={surveyId} options={surveys} optionLabel="title" optionValue="value" onChange={(e) => setSurveyId(e.value)} placeholder="Pesquisa" className="p-inputtext-sm" style={{ minWidth: 220 }} />
                        <Dropdown value={departmentId} options={departments} optionLabel="title" optionValue="value" onChange={(e) => setDepartmentId(e.value)} placeholder="Departamento" className="p-inputtext-sm" style={{ minWidth: 220 }} />
                    </div>

                    <ul className="nav nav-tabs mb-3">
                        {TABS.map((tab) => (
                            <li className="nav-item" key={tab.key}>
                                {activeTab === tab.key ? (
                                    <span className="nav-link active">{tab.label}</span>
                                ) : (
                                    <button type="button" className="nav-link text-green border-0 bg-transparent" onClick={() => setActiveTab(tab.key)}>
                                        {tab.label}
                                    </button>
                                )}
                            </li>
                        ))}
                    </ul>

                    <div className="bg-white rounded border-clean p-3">
                        {loading ? (
                            <div className="text-center py-5 text-muted">Carregando…</div>
                        ) : dispatches.length === 0 ? (
                            <div className="text-center py-5 text-muted">Nenhum registro nesta aba.</div>
                        ) : (
                            <div className="table-responsive">
                                <table className="table table-hover align-middle">
                                    <thead>
                                        <tr>
                                            <th>Colaborador</th>
                                            <th>Pesquisa</th>
                                            <th>Gatilho</th>
                                            <th>Disparo</th>
                                            <th>Prazo</th>
                                            <th>Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {dispatches.map((d) => (
                                            <tr key={d.id}>
                                                <td>{d.employee_name}</td>
                                                <td>{d.survey_name}</td>
                                                <td className="small">{d.trigger_label}</td>
                                                <td className="small">{new Date(d.data_disparo).toLocaleString('pt-BR')}</td>
                                                <td className="small">{new Date(d.prazo_resposta).toLocaleDateString('pt-BR')}</td>
                                                <td>
                                                    <Chip label={d.status_display || d.status} className={STATUS_CHIP[d.status] || 'bg-secondary text-white border-0'} />
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </ProtectedRoute>
    );
}
